import React, { useRef, useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { ArrowLeft, Pill, AlertTriangle, Lock, XCircle, Printer } from 'lucide-react'
import DashboardLayout from '../layouts/DashboardLayout'
import SignaturePad from '../components/SignaturePad'
import { cn } from '../lib/utils'

// The prescription pad. Two design decisions worth stating:
//
//  1. THE ALLERGY IS AT THE TOP, in red, before the medicine box — not in the patient file the
//     prescriber would have to go and look at. Prescribing amoxicillin to a penicillin-allergic
//     patient is the classic dental harm, and it happens when the information is one click away
//     instead of in front of you.
//  2. IT SIGNS IN THE SAME ACTION. A script that sits unsigned is not a script, and making
//     signing a second step is how a blank one ends up handed over.

export default function Prescriptions({ patient = {}, alerts = {}, prescriber = {}, prescriptions = [] }) {
  const [v, setV] = useState({ items: '', diagnosis: '', notes: '', allergies_checked: '', pregnancy_checked: false })
  const [sig, setSig] = useState('')
  const [saving, setSaving] = useState(false)
  const set = (k, val) => setV((p) => ({ ...p, [k]: val }))

  const hasAlert = alerts.allergies || alerts.pregnant || alerts.chronic || alerts.medications

  const submit = (withSignature) => {
    if (!v.items.trim()) { window.alert('Write at least one medicine.'); return }
    if (withSignature && !sig) { window.alert('Sign the script before issuing it.'); return }
    if (withSignature && alerts.allergies && !v.allergies_checked.trim()) {
      if (!window.confirm(`This patient has a recorded allergy:\n\n${alerts.allergies}\n\nYou have not noted what you checked. Issue anyway?`)) return
    }
    setSaving(true)
    router.post(`/patients/${patient.id}/prescriptions`,
      { prescription: v, signature: withSignature ? sig : null, signed_name: prescriber.name },
      { preserveScroll: true, onFinish: () => setSaving(false),
        onSuccess: () => { setV({ items: '', diagnosis: '', notes: '', allergies_checked: '', pregnancy_checked: false }); setSig('') } })
  }

  const cancel = (rx) => {
    const reason = window.prompt('Why is this prescription being cancelled?\n\nThe original stays on the record — the pharmacy may already hold a copy.')
    if (!reason || !reason.trim()) return
    router.post(`/patients/${patient.id}/prescriptions/${rx.id}/cancel`, { reason }, { preserveScroll: true })
  }

  return (
    <DashboardLayout>
      <Link href={`/patients/${patient.id}`} className="mb-4 inline-flex items-center gap-1 text-sm text-brand-muted hover:text-brand-ink">
        <ArrowLeft size={14} /> {patient.name}
      </Link>

      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-primary">
          <Pill size={18} className="text-white" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-brand-ink">Prescription pad — {patient.name}</h1>
          <p className="text-sm text-brand-muted">
            {prescriber.name} · {prescriber.registration}{patient.date_of_birth ? ` · patient DOB ${patient.date_of_birth}` : ''}
          </p>
        </div>
      </div>

      {/* The alert goes FIRST, before the medicine box. */}
      {hasAlert && (
        <div className="mb-4 rounded-xl border-2 border-red-300 bg-red-50 p-4">
          <p className="flex items-center gap-1.5 text-sm font-bold text-red-800">
            <AlertTriangle size={15} /> Check before you prescribe
          </p>
          <dl className="mt-2 grid grid-cols-[130px_1fr] gap-x-3 gap-y-1 text-sm">
            {alerts.allergies && (<><dt className="font-semibold text-red-800">Allergies</dt><dd className="font-semibold text-red-900">{alerts.allergies}</dd></>)}
            {alerts.pregnant && (<><dt className="font-semibold text-red-800">Pregnancy</dt><dd className="font-semibold text-red-900">Recorded as pregnant</dd></>)}
            {alerts.chronic && (<><dt className="text-red-800">Conditions</dt><dd className="text-red-900">{alerts.chronic}</dd></>)}
            {alerts.medications && (<><dt className="text-red-800">On medication</dt><dd className="text-red-900">{alerts.medications}</dd></>)}
          </dl>
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-[1fr_380px]">
        <div className="rounded-xl border border-brand-border bg-white p-5">
          <label className="mb-1 block text-sm font-semibold text-brand-ink">Medicines — one per line</label>
          <textarea rows={6} value={v.items} onChange={(e) => set('items', e.target.value)}
            placeholder={"Amoxicillin 500mg caps — 1 tds x 5 days (15)\nIbuprofen 400mg — 1 tds prn pain (20)"}
            className="w-full rounded-lg border border-brand-border p-3 font-mono text-sm focus:border-brand-primary focus:outline-none" />
          <p className="mt-1 text-[11px] text-brand-muted">Written exactly as you write it — nothing is auto-corrected or re-formatted.</p>

          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold text-brand-muted">For (diagnosis)</label>
              <input value={v.diagnosis} onChange={(e) => set('diagnosis', e.target.value)}
                className="w-full rounded-lg border border-brand-border p-2 text-sm" placeholder="e.g. acute apical abscess 46" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-brand-muted">Allergy check</label>
              <input value={v.allergies_checked} onChange={(e) => set('allergies_checked', e.target.value)}
                className={cn('w-full rounded-lg border p-2 text-sm', alerts.allergies && !v.allergies_checked.trim() ? 'border-red-300 bg-red-50' : 'border-brand-border')}
                placeholder={alerts.allergies ? 'What did you check?' : 'None known — confirmed with patient'} />
            </div>
          </div>

          <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm text-brand-ink">
            <input type="checkbox" checked={v.pregnancy_checked} onChange={(e) => set('pregnancy_checked', e.target.checked)} className="h-4 w-4" />
            I asked about pregnancy / breastfeeding
          </label>

          <div className="mt-3">
            <label className="mb-1 block text-xs font-semibold text-brand-muted">Notes to the pharmacist <span className="font-normal">(optional)</span></label>
            <input value={v.notes} onChange={(e) => set('notes', e.target.value)} className="w-full rounded-lg border border-brand-border p-2 text-sm" />
          </div>

          <div className="mt-4">
            <label className="mb-1 block text-sm font-semibold text-brand-ink">Your signature</label>
            <SignaturePad onChange={({ dataUrl }) => setSig(dataUrl)} label="Sign here to issue the script" />
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={() => submit(true)} disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-primary px-4 py-2 text-sm font-semibold text-white hover:brightness-95 disabled:opacity-50">
              <Pill size={14} /> {saving ? 'Saving…' : 'Sign & issue'}
            </button>
            <button onClick={() => submit(false)} disabled={saving}
              className="rounded-lg border border-brand-border px-4 py-2 text-sm font-semibold text-brand-ink hover:bg-brand-surface disabled:opacity-50">
              Save unsigned draft
            </button>
          </div>
        </div>

        <div>
          <h2 className="mb-2 text-sm font-semibold text-brand-ink">On the record ({prescriptions.length})</h2>
          <div className="space-y-3">
            {prescriptions.length === 0 && (
              <p className="rounded-xl border border-brand-border bg-white p-4 text-sm text-brand-muted">Nothing prescribed yet.</p>
            )}
            {prescriptions.map((r) => (
              <div key={r.id} className={cn('rounded-xl border bg-white p-4', r.cancelled ? 'border-red-200 opacity-75' : 'border-brand-border')}>
                <div className="mb-1.5 flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-brand-ink">{r.issued_at}</span>
                  {r.signed ? (
                    <span className="inline-flex items-center gap-1 rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700"><Lock size={10} /> Signed</span>
                  ) : (
                    <span className="rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[11px] font-semibold text-amber-700">Unsigned draft</span>
                  )}
                  {r.cancelled && <span className="rounded border border-red-300 bg-red-50 px-1.5 py-0.5 text-[11px] font-bold text-red-700">CANCELLED</span>}
                  {!r.intact && <span className="rounded border border-red-300 bg-red-50 px-1.5 py-0.5 text-[11px] font-bold text-red-700">⚠ ALTERED</span>}
                </div>
                <ul className="mb-1 list-disc pl-4 text-sm text-brand-ink">
                  {r.medicines.map((m, i) => <li key={i} className="font-mono text-xs">{m}</li>)}
                </ul>
                {r.diagnosis && <p className="text-xs text-brand-muted">For: {r.diagnosis}</p>}
                {r.cancelled_reason && <p className="mt-1 text-xs text-red-700">{r.cancelled_reason}</p>}
                <p className="mt-1 text-[11px] text-brand-muted">{r.prescriber_name} · {r.prescriber_registration}</p>
                {!r.cancelled && (
                  <button onClick={() => cancel(r)} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-red-700 hover:underline">
                    <XCircle size={11} /> Cancel this script
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
